import { action, makeObservable, observable } from "mobx";
import CategoryAPI from "../api/CategoryAPI";
import {
  Category,
  CategoryUpsertPayload,
  GetCategoriesParams,
} from "../models/Categories/types";

export class CategoryStore {
  categoryAPI: CategoryAPI;
  categories: Category[] = [];
  categoryDetail: Category | null = null;
  limit = 0;
  total_pages = 0;
  total_count = 0;
  error: string | null = null;

  constructor() {
    this.categoryAPI = new CategoryAPI();
    makeObservable(this, {
      categories: observable,
      categoryDetail: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      error: observable,
      clearCategoryDetail: action,
      listCategories: action,
      getCategoryById: action,
      createCategoryRecord: action,
      updateCategoryRecord: action,
      deleteCategoryRecord: action,
    });
  }

  clearCategoryDetail = () => {
    this.categoryDetail = null;
  };

  listCategories = async (params: GetCategoriesParams) => {
    try {
      const response = await this.categoryAPI.listCategories(params);
      const lim = response.limit || params.limit || 30;
      let list: Category[] = response.list;
      let total_count = response.total_count;
      let total_pages = response.total_pages;

      /** Fallback when API omits `active` filtering — narrows the current page. */
      if (params.active !== undefined) {
        const want = params.active;
        const prevLen = list.length;
        const filtered = list.filter((c) => Boolean(c.active) === want);
        if (filtered.length < prevLen) {
          total_count = filtered.length;
          total_pages =
            filtered.length > 0
              ? Math.max(1, Math.ceil(filtered.length / lim))
              : 0;
        }
        list = filtered;
      }

      this.limit = response.limit;
      this.total_pages = total_pages;
      this.total_count = total_count;
      this.categories = list;
      this.error = null;
    } catch (error: any) {
      this.error =
        error?.response?.data?.message ||
        "Something went wrong loading categories.";
      throw error;
    }
  };

  getCategoryById = async (id: number) => {
    try {
      const category = await this.categoryAPI.getCategoryById(id);
      this.categoryDetail = category;
      return category;
    } catch (error: any) {
      throw error;
    }
  };

  createCategoryRecord = async (body: CategoryUpsertPayload) => {
    try {
      return await this.categoryAPI.createCategory(body);
    } catch (error: any) {
      throw error;
    }
  };

  updateCategoryRecord = async (id: number, body: CategoryUpsertPayload) => {
    try {
      return await this.categoryAPI.updateCategory(id, body);
    } catch (error: any) {
      throw error;
    }
  };

  deleteCategoryRecord = async (id: number) => {
    try {
      return await this.categoryAPI.deleteCategory(id);
    } catch (error: any) {
      throw error;
    }
  };
}
